import type { StatFilter } from '@mtg-deck-tool/shared';
import { useSearchStore } from '../../stores/search-store.js';
import { useBarClause } from '../../hooks/useBarClause.js';

const COMPARATORS: Array<{ value: '<' | '=' | '>'; label: string }> = [
  { value: '<', label: '<' },
  { value: '=', label: '=' },
  { value: '>', label: '>' },
];

const selectClass =
  'bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-gray-300 focus:outline-none focus:border-orange-500 transition-colors cursor-pointer';

const inputClass =
  'bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-gray-300 placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors w-16';

interface StatRowProps {
  label: string;
  statKey: 'power' | 'toughness' | 'manaValue';
}

const STAT_PREFIX_MAP: Record<string, string> = {
  power: 'pow',
  toughness: 'tou',
  manaValue: 'mv',
};

function StatRow({ label, statKey }: StatRowProps) {
  const stats = useSearchStore((s) => s.advancedFilters.stats);
  const setAdvancedFilter = useSearchStore((s) => s.setAdvancedFilter);
  const { writeClause } = useBarClause();
  const current = stats[statKey];

  const base = STAT_PREFIX_MAP[statKey];

  const syncBar = (filter: StatFilter | null) => {
    // Remove all comparator variants
    writeClause(`${base}>`, null);
    writeClause(`${base}=`, null);
    writeClause(`${base}<`, null);
    // Write the active one if filter is set
    if (filter) {
      writeClause(`${base}${filter.comparator}`, String(filter.value));
    }
  };

  const update = (filter: StatFilter | null) => {
    setAdvancedFilter('stats', { ...stats, [statKey]: filter });
    syncBar(filter);
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-400 w-20 shrink-0">{label}</span>
      <select
        value={current?.comparator ?? '='}
        onChange={(e) => {
          const comp = e.target.value as '<' | '=' | '>';
          if (current) {
            update({ ...current, comparator: comp });
          } else {
            update({ comparator: comp, value: 0 });
          }
        }}
        className={selectClass}
      >
        {COMPARATORS.map((c) => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
      <input
        type="number"
        min={0}
        value={current?.value ?? ''}
        onChange={(e) => {
          const val = e.target.value;
          if (val === '') {
            update(null);
          } else {
            const num = parseInt(val, 10);
            update({ comparator: current?.comparator ?? '=', value: num });
          }
        }}
        placeholder="—"
        className={inputClass}
      />
    </div>
  );
}

export function StatsFilters() {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] text-gray-500 uppercase tracking-wider">Stats</label>
      <div className="space-y-1.5">
        <StatRow label="Power" statKey="power" />
        <StatRow label="Toughness" statKey="toughness" />
        <StatRow label="Mana Value" statKey="manaValue" />
      </div>
    </div>
  );
}
